import { isAiTag } from '../ImageMediaTags/MediaTagChip';

/**
 * What the viewer's media tags on a phone share: the row of chips at the
 * bottom of the photo (MobileMediaTagRow) and the two sheets it opens, one
 * for a tag (MobileMediaTagSheet) and one to add a tag (MobileAddMediaTagSheet).
 */

/** DOM id of the sheet a tag's chip opens. */
export const VIEWER_TAG_SHEET_ID = 'viewer-media-tag-sheet';

/** DOM id of the sheet the '+' chip opens. */
export const VIEWER_ADD_TAG_SHEET_ID = 'viewer-add-media-tag-sheet';

/**
 * A tag as the chips and the lists name it: an assignment's tag leads with
 * the assignment's number path ("1.2 Laatoitus"), as the desktop chips do.
 * Takes a photo's tag link (selectSelectedFilepermissionMediaTags) or an
 * option to add (selectGroupedProjectMediaTags) alike.
 * @param {{name?: string, assignmentNumberPath?: string}} tag
 * @returns {string}
 */
export const getMediaTagLabel = (tag) =>
  [tag.assignmentNumberPath, tag.name].filter(Boolean).join(' ');

/**
 * The photo's tags in the row's order: a person's own first and the AI
 * tagger's after them, each group in the store's order, as the desktop strip
 * orders them. A link whose tag has no name (the tag is not in the store) or
 * that is deleted is left out.
 * @param {Array<Object>} tags the photo's tag links, with their tags' names
 * @returns {Array<Object>}
 */
export const orderMediaTagsForRow = (tags) => {
  const shown = (tags || []).filter((tag) => tag && tag.name && !tag.deletedAt);
  return [...shown.filter((tag) => !isAiTag(tag)), ...shown.filter(isAiTag)];
};

/**
 * The options whose label holds what is typed, any case, in their order (so
 * their groups keep theirs). With nothing typed, all of them: the same array.
 * @param {Array<Object>} options
 * @param {string} query
 * @returns {Array<Object>}
 */
export const filterMediaTagOptions = (options, query) => {
  const needle = (query || '').trim().toLowerCase();
  if (!needle) return options;
  return options.filter((option) =>
    getMediaTagLabel(option).toLowerCase().includes(needle)
  );
};

/**
 * The options under their groups' names (`groupIdentifier`: the free-form
 * tags, the assignments), the groups in the order they first come.
 * @param {Array<Object>} options
 * @returns {Array<{group: string, options: Array<Object>}>}
 */
export const groupMediaTagOptions = (options) => {
  const groups = [];
  const byName = new Map();
  options.forEach((option) => {
    const name = option.groupIdentifier || '';
    if (!byName.has(name)) {
      const group = { group: name, options: [] };
      byName.set(name, group);
      groups.push(group);
    }
    byName.get(name).options.push(option);
  });
  return groups;
};

const toComparableName = (name) =>
  typeof name === 'string' ? name.trim().toLowerCase() : '';

/**
 * The first tag named `name`, ignoring the case and the spaces around it; a
 * blank name finds none.
 * @param {Array<{name?: string}>} tags
 * @param {string} name
 * @returns {Object|undefined}
 */
export const findMediaTagByName = (tags, name) => {
  const wanted = toComparableName(name);
  if (!wanted) return undefined;
  return (tags || []).find(
    (tag) => tag && toComparableName(tag.name) === wanted
  );
};

/**
 * z-index of the tag sheets: above the viewer, which is a dialog
 * (theme.zIndex.modal), where the bank's own sheets would open behind it,
 * and under the snackbars, so an error they report paints over them.
 * @param {Object} theme MUI theme
 * @returns {number}
 */
export const getViewerSheetZIndex = (theme) => theme.zIndex.modal + 1;

const stopPropagation = (event) => event.stopPropagation();

const stopOneFingerTouch = (event) => {
  if (event.touches.length < 2) event.stopPropagation();
};

/**
 * Handlers for a part of the viewer drawn over the photo (the comments and
 * the tag row below md) that keep what happens there from the photo's own
 * handlers: on a desktop a click zooms the photo, a drag pans it and the
 * wheel zooms it, and on a phone one finger pages to the next photo. A
 * touch of two fingers still reaches the photo, so a pinch that starts on
 * the part zooms it.
 */
export const VIEWER_OVERLAY_GUARDS = Object.freeze({
  onClick: stopPropagation,
  onDoubleClick: stopPropagation,
  onMouseDown: stopPropagation,
  onMouseMove: stopPropagation,
  onMouseUp: stopPropagation,
  onWheel: stopPropagation,
  onTouchStart: stopOneFingerTouch,
  onTouchMove: stopOneFingerTouch,
  onTouchEnd: stopOneFingerTouch,
});
